import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { verifyAuthenticatedUser, useInvitationCode } from '../services/prisonerService';
import { fetchPlayerWinRate } from '../services/prisonerGameService';

/**
 * 用户钩子，提供统一的用户信息和认证状态管理
 * 确保只有经过认证的用户才能访问需要身份验证的功能
 * 
 * @returns {Object} 包含用户信息、认证状态和操作方法的对象
 */
export const useUser = () => {
  const auth = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [activationError, setActivationError] = useState(null);
  const [activationSuccess, setActivationSuccess] = useState(false);
  const [winRate, setWinRate] = useState(null);
  const [isLoadingWinRate, setIsLoadingWinRate] = useState(false);
  const [dayPlay, setDayPlay] = useState(false);
  const [dailyGameInfo, setDailyGameInfo] = useState(null);
  const [dailyGameCount, setDailyGameCount] = useState(0);
  const [dailyGameLimit, setDailyGameLimit] = useState(1);
  const [remainingDailyGames, setRemainingDailyGames] = useState(1);
  
  // 用户认证状态
  const isAuthenticated = !!auth.user && !!auth.user.address;
  
  // 安全获取用户ID
  const userId = isAuthenticated ? auth.user.address : null;
  
  // 验证用户激活状态
  const verifyUserActivation = useCallback(async (address) => {
    if (!address) return false;
    
    try {
      setIsVerifying(true);
      const result = await verifyAuthenticatedUser(address);
      const activated = result?.is_valid || false;

      // 获取当天游戏状态
      if (result?.day_played !== undefined) {
        setDayPlay(result.day_played);
      }

      // 获取详细的每日游戏信息
      if (result?.user_data?.daily_game_info) {
        const gameInfo = result.user_data.daily_game_info;
        setDailyGameInfo(gameInfo);
        setDailyGameCount(parseInt(gameInfo.completed_count) || 0);
        setDailyGameLimit(parseInt(gameInfo.daily_limit) || 1);
        setRemainingDailyGames(parseInt(gameInfo.remaining_count) || 0);
      }
      
      setIsActivated(activated);
      return activated;
    } catch (error) {
      console.error('验证用户激活状态失败:', error);
      setIsActivated(false);
      return false;
    } finally {
      setIsVerifying(false);
    }
  }, []);
  
  // 提交激活码
  const submitActivationCode = useCallback(async (code) => {
    if (!auth.user?.address) {
      setActivationError('请先连接钱包');
      return { success: false, message: '请先连接钱包' };
    }
    
    try {
      setActivationError(null);
      setActivationSuccess(false);
      
      const result = await useInvitationCode(code, auth.user.address);
      
      if (result.success) {
        setIsActivated(true);
        setActivationSuccess(true);
      } else {
        setActivationError(result.message || '激活失败');
      }
      
      return result;
    } catch (error) {
      console.error('提交激活码失败:', error);
      setActivationError(error.message || '提交激活码时出错');
      return { success: false, message: error.message || '提交激活码时出错' };
    }
  }, [auth.user?.address]);
  
  // 获取用户胜率
  const loadWinRate = useCallback(async (address) => {
    return 0
    if (!address) return null;
    
    try {
      setIsLoadingWinRate(true);
      const data = await fetchPlayerWinRate(address);
      const winRateData = data?.win_rate_display || null;
      setWinRate(winRateData);
      return winRateData;
    } catch (error) {
      console.error('获取用户胜率失败:', error);
      return null;
    } finally {
      setIsLoadingWinRate(false);
    }
  }, []);
  
  // 初始加载时验证用户状态
  useEffect(() => {
    const checkUserStatus = async () => {
      // 简单延迟以确保认证状态已完全加载
      const timer = setTimeout(async () => {
        setIsReady(true);
        
        // 如果已登录，验证激活状态
        if (isAuthenticated && auth.user?.address) {
          await verifyUserActivation(auth.user.address);
          await loadWinRate(auth.user.address);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    };
    
    checkUserStatus();
  }, [auth.user, isAuthenticated, verifyUserActivation, loadWinRate]);
  
  /**
   * 要求用户认证
   * 如果用户未认证，提示连接钱包
   */
  const requireAuth = async () => {
    if (isAuthenticated) return true;
    
    // 检查钱包可用性
    const { ethereum, solana } = auth.checkWalletAvailability();
    
    if (ethereum) {
      return auth.connectEthereumWallet();
    } else if (solana) {
      return auth.connectSolanaWallet();
    } else {
      throw new Error("未检测到支持的钱包，请安装MetaMask或Phantom钱包");
    }
  };
  
  /**
   * 安全地执行需要用户认证的操作
   * @param {Function} operation 需要执行的操作函数
   * @param {boolean} autoConnect 是否自动尝试连接钱包
   * @returns {Promise<any>} 操作结果
   */
  const withAuth = async (operation, autoConnect = true) => {
    if (!isAuthenticated) {
      if (autoConnect) {
        try {
          const authResult = await requireAuth();
          if (!authResult.success) {
            throw new Error("用户认证失败");
          }
        } catch (error) {
          console.error("认证失败:", error);
          throw new Error("请先连接钱包后再执行此操作");
        }
      } else {
        throw new Error("请先连接钱包后再执行此操作");
      }
    }
    
    return operation();
  };
  
  return {
    // 用户信息
    user: auth.user,
    userId,
    isAuthenticated,
    isActivated,
    userActive: isActivated,  // 从web3_auth表active字段获取的实际激活状态
    isLoading: auth.isLoading,
    isVerifying,
    isReady,
    error: auth.error,
    activationError,
    activationSuccess,
    
    // 游戏次数相关 - 可配置的每日游戏次数限制
    dayPlay, // 是否达到当天限制，true表示已达到限制，false表示未达到
    dailyGameInfo, // 完整的游戏信息对象
    dailyGameCount, // 当天已完成的游戏次数
    dailyGameLimit, // 当天允许的最大游戏次数
    remainingDailyGames, // 当天剩余游戏次数
    
    // 胜率相关
    winRate,
    isLoadingWinRate,
    loadWinRate,
    
    // 操作方法
    connectEthereumWallet: auth.connectEthereumWallet,
    connectSolanaWallet: auth.connectSolanaWallet,
    disconnectWallet: auth.disconnectWallet,
    checkWalletAvailability: auth.checkWalletAvailability,
    verifyUserActivation,
    submitActivationCode,
    requireAuth,
    withAuth,
  };
};
